import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  Theme,
  Tooltip,
} from '@material-ui/core';
import { Error } from '@material-ui/icons';
import { formatCurrency } from '@roc/ui/utils';
import { YesNoChip } from '@roc/ui';
import { formatDateIgnoreTimezone } from '@roc/feature-utils';

const useStyles = makeStyles((theme: Theme) => ({
  titleCell: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
    fontWeight: 'bold',
  },
  labelCell: {
    width: '40%',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  valueCell: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  matchCell: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
    width: '5%',
  },
  lastRow: {
    '& td': {
      borderBottom: 'none',
    },
  },
}));

type FieldType = 'currency' | 'date' | 'boolean' | 'text';

interface FieldRow {
  label: string;
  extractedKey: string;
  expectedKey?: string;
  matchKey?: string;
  type: FieldType;
  mismatchTooltip?: string;
  required?: boolean;
}

const DEFAULT_MISMATCH_TOOLTIP = 'The extracted value does not match the portal value.';
const MISSING_REQUIRED_TOOLTIP = 'Required field not found in the document.';

// Final Title Policy has its own field set on the backend (see
// TrailingDocsResponseParser.parseFinalTitlePolicy / TrailingDocsMatcher),
// distinct from RECORDED_MORTGAGE/RECORDED_ALR (see RecordedDocumentReviewTable).
// Rows with an expectedKey compare the extracted value against the portal
// value and show the mismatch icon: a matchKey drives it from a precomputed
// backend flag, otherwise it's derived client-side (see valuesMismatch).
// Point Four Loan Amount compares against the same portal loan amount used
// by the top-level Loan Amount row, computed client-side (no backend flag).
// Point Four Loan Funder Series compares against the portal's own loan id
// (the Series designation on a Roc Capital loan is expected to equal the
// loan id) via the backend-computed pointFourSeriesMatches flag.
const FIELD_ROWS: FieldRow[] = [
  {
    label: 'Loan Amount',
    extractedKey: 'extractedLoanAmount',
    expectedKey: 'portalLoanAmount',
    matchKey: 'loanAmountsMatch',
    type: 'currency',
  },
  { label: 'Date of Policy', extractedKey: 'extractedDate', type: 'date', required: true },
  {
    label: 'Closing Date',
    extractedKey: 'extractedClosingDate',
    expectedKey: 'portalClosingDate',
    matchKey: 'closingDatesMatch',
    type: 'date',
    required: true,
  },
  { label: 'Policy Date On/After Closing', extractedKey: 'policyDateOnOrAfterClosing', type: 'boolean' },
  {
    label: 'Property Address',
    extractedKey: 'extractedAddress',
    expectedKey: 'portalAddress',
    matchKey: 'propertyAddressesMatch',
    type: 'text',
  },
  {
    label: 'Entity Name',
    extractedKey: 'extractedEntityName',
    expectedKey: 'portalEntityName',
    matchKey: 'entityNamesMatch',
    type: 'text',
  },
  {
    label: 'Lender Name',
    extractedKey: 'extractedLenderName',
    expectedKey: 'portalLenderName',
    matchKey: 'lenderNamesMatch',
    type: 'text',
  },
  { label: 'Did Agent Sign', extractedKey: 'didAgentSign', type: 'boolean' },
  { label: 'Is Short Form', extractedKey: 'isShortForm', type: 'boolean' },
  {
    label: 'Policy Number',
    extractedKey: 'policyNumber',
    matchKey: 'policyNumberFormatValid',
    type: 'text',
    mismatchTooltip: 'Policy Number must contain at least one digit (numeric or alphanumeric, not purely alphabetic).',
  },
  { label: 'Legal Description Present', extractedKey: 'legalDescriptionPresent', type: 'boolean' },
  { label: 'Schedule A Item 4 (Point Four)', extractedKey: 'pointFour', type: 'text' },
  {
    label: 'Point Four Loan Amount',
    extractedKey: 'pointFourAmount',
    expectedKey: 'portalLoanAmount',
    type: 'currency',
  },
  { label: 'Point Four Recorded Date', extractedKey: 'pointFourRecordedDate', type: 'date' },
  {
    label: 'Point Four Entity Name',
    extractedKey: 'pointFourEntityName',
    expectedKey: 'portalEntityName',
    matchKey: 'pointFourEntityNameMatches',
    type: 'text',
    mismatchTooltip: 'The Point Four mortgagor does not appear to be the same entity as the portal borrower (LLM comparison).',
  },
  {
    label: 'Point Four Loan Funder Series',
    extractedKey: 'pointFourLoanFunderSeries',
    expectedKey: 'portalLoanId',
    matchKey: 'pointFourSeriesMatches',
    type: 'text',
  },
  { label: 'Post Closer', extractedKey: 'postCloserName', type: 'text' },
];

const isNullish = (value: any) => value === null || value === undefined;

// Short form title policies incorporate the legal description by reference,
// so it's always considered present regardless of what was extracted.
const FORCE_YES_ON_SHORT_FORM = new Set(['legalDescriptionPresent', 'pointFour',
  'pointFourLoanFunderSeries']);

const formatCell = (type: FieldType, value: any) => {
  if (isNullish(value)) return '—';
  if (type === 'boolean') return <YesNoChip value={value ? 'Yes' : 'No'} />;
  if (type === 'currency') return formatCurrency(Number(value));
  if (type === 'date') return formatDateIgnoreTimezone(value);
  return String(value);
};

// Used for rows with an expectedKey but no backend-computed matchKey: the
// mismatch icon is derived directly from the two displayed values instead of
// a precomputed flag.
const valuesMismatch = (type: FieldType, extracted: any, expected: any) => {
  if (isNullish(extracted) || isNullish(expected)) return false;
  if (type === 'currency') return Number(extracted) !== Number(expected);
  if (type === 'date') return formatDateIgnoreTimezone(extracted) !== formatDateIgnoreTimezone(expected);
  return String(extracted) !== String(expected);
};

export interface FinalTitlePolicyReviewTableProps {
  data: Record<string, any>;
}

export const FinalTitlePolicyReviewTable = ({ data }: FinalTitlePolicyReviewTableProps) => {
  const classes = useStyles();
  const isShortForm = data.isShortForm === true;
  const rows = FIELD_ROWS
    .map(row => {
      const forceYes = isShortForm && FORCE_YES_ON_SHORT_FORM.has(row.extractedKey);
      const extractedValue = forceYes ? true : data[row.extractedKey];
      const expectedValue = row.expectedKey ? data[row.expectedKey] : undefined;
      const missingRequired = !!row.required && isNullish(extractedValue);
      const isMismatch = missingRequired
        ? true
        : row.matchKey
        ? data[row.matchKey] === false
        : !!row.expectedKey && valuesMismatch(row.type, extractedValue, expectedValue);
      const mismatchTooltip = missingRequired ? MISSING_REQUIRED_TOOLTIP : row.mismatchTooltip;
      return { ...row, extractedValue, expectedValue, isMismatch, mismatchTooltip };
    })
    .filter(row => row.required || !(isNullish(row.extractedValue) && isNullish(row.expectedValue)));

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell className={classes.matchCell} />
          <TableCell className={classes.titleCell}>Field</TableCell>
          <TableCell className={classes.titleCell}>Portal</TableCell>
          <TableCell className={classes.titleCell}>Document</TableCell>
        </TableRow>
        {rows.map((row, index) => (
          <TableRow key={row.extractedKey} className={index === rows.length - 1 ? classes.lastRow : ''}>
            <TableCell className={classes.matchCell}>
              {row.isMismatch && (
                <Tooltip title={row.mismatchTooltip ?? DEFAULT_MISMATCH_TOOLTIP}>
                  <Error color="error" />
                </Tooltip>
              )}
            </TableCell>
            <TableCell className={classes.labelCell}>{row.label}</TableCell>
            <TableCell className={classes.valueCell}>
              {row.expectedKey ? formatCell(row.type, row.expectedValue) : '—'}
            </TableCell>
            <TableCell className={classes.valueCell}>
              {formatCell(row.type, row.extractedValue)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
